import { Box, Divider, Grid, TextField, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../../../api/hooks/useAPI";
import { useCheckUserPermission } from "../../../../../../api/hooks/useCheckUserPermission";
import useColors from "../../../../../../api/hooks/useColors";
import { remediationKeys } from "../../../../../../api/queryKeys";
import CustomToolTipWithPermission from "../../../../../../components/CustomToolTipWithPermission";
import CustomToolTip from "../../../../../../components/CustomTooltip";
import LabelComponentProp from "../../../../../../components/LabelComponentProp";
import StatusChip from "../../../../../../components/chips/StatusChip";
import { RemediationTypeChip } from "../../../../../../utils/chipsData";
import {
  PLATFORM_USER_ACTIONS,
  RemediationType,
} from "../../../../../../utils/constants";
import ChipColor from "../../../../../remediation/plan/components/ChipColor";
import CustomPrioritySelect from "../../../../../remediation/plan/components/CustomPrioritySelect";
import RemediationBreakdown from "../../../../../remediation/plan/components/RemediationBreakdown";
import IntegrationImage from "../../../../../stack/integrations/components/IntegrationImage";
import ImpactChip from "../control/ImpactChip";

//to open the popover on click event we need create this component and pass the event to the parent component
const ResilienceImpactBox = ({
  demoMode,
  handlePopoverOpen,
  selectedRemediation,
}) => (
  <Grid item xs={2.5}>
    {demoMode ? (
      <CustomToolTip title="View breakdown">
        <Box
          key={"impactBox"}
          onClick={(e) => {
            handlePopoverOpen(e);
          }}
          style={{ cursor: "pointer" }}
        >
          <LabelComponentProp label={"Resilience Impact"}>
            <ChipColor
              value={selectedRemediation?.impact}
              type="impact"
              width={90}
            />
          </LabelComponentProp>
        </Box>
      </CustomToolTip>
    ) : (
      <Box key={"impactBox"}>
        <LabelComponentProp label={"Resilience Impact"}>
          <ChipColor
            value={selectedRemediation?.impact}
            type="impact"
            width={90}
          />
        </LabelComponentProp>
      </Box>
    )}
  </Grid>
);

//to open the popover on click event we need create this component and pass the event to the parent component
const FeasibilityBox = ({
  demoMode,
  selectedRemediation,
  handleFeasibilityPopoverOpen,
}) => (
  <Grid item xs={2.5}>
    {demoMode ? (
      <CustomToolTip title="View breakdown">
        <Box
          key={"feasibilityBox"}
          onClick={(e) => handleFeasibilityPopoverOpen(e)}
          sx={{ cursor: "pointer" }}
        >
          <LabelComponentProp label={"Feasibility"}>
            <ChipColor
              value={selectedRemediation?.feasibility}
              type="feasibility"
              width={90}
            />
          </LabelComponentProp>
        </Box>
      </CustomToolTip>
    ) : (
      <Box key={"feasibilityBox"}>
        <LabelComponentProp label={"Feasibility"}>
          <ChipColor
            value={selectedRemediation?.feasibility}
            type="feasibility"
            width={90}
          />
        </LabelComponentProp>
      </Box>
    )}
  </Grid>
);

const RemediationDetailsSection = ({
  selectedRemediation,
  demoMode,
  fullControl,
}) => {
  const colors = useColors();

  const [editCost, setEditCost] = useState(false);
  const { updateRemediationCost } = useAPI();
  const [anchorElImpact, setAnchorElImpact] = useState(null);
  const queryClient = useQueryClient();

  const { permitted: hasPermission } = useCheckUserPermission(
    PLATFORM_USER_ACTIONS.UPDATE_REMEDIATION_COST
  );

  const mutateCost = useMutation({
    mutationFn: updateRemediationCost,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: remediationKeys.all });
      setEditCost(false);
    },
  });
  const handlePopoverClose = () => {
    setAnchorElImpact(null);
  };

  const handlePopoverOpen = (event) => {
    setAnchorElImpact(event.currentTarget);
  };

  const inputSx = {
    "& input[type=number]": {
      "-moz-appearance": "textfield",
      "text-align": "center",
    },
    "& input[type=number]::-webkit-outer-spin-button": {
      "-webkit-appearance": "none",
      margin: 0,
    },
    "& input[type=number]::-webkit-inner-spin-button": {
      "-webkit-appearance": "none",
      margin: 0,
    },
  };

  const handleCostChange = (value) => {
    const cost = parseInt(value);

    mutateCost.mutate({
      remediation_id: selectedRemediation?.remediation_id,
      cost: cost,
    });
  };

  const ToolBox = () => (
    <LabelComponentProp
      label={"Tool"}
      textTypography="caption"
      sx={{
        display: "flex",
        alignItems: "left",
      }}
    >
      <IntegrationImage
        name={selectedRemediation?.tool}
        width={"24px"}
        newLogo={true}
      />
    </LabelComponentProp>
  );

  const PriorityBox = () => (
    <LabelComponentProp
      label={"Priority"}
      textTypography="caption"
      sx={{
        display: "flex",
        alignItems: "left",
      }}
    >
      <CustomPrioritySelect
        row={selectedRemediation}
        renderValue={(customPriority) => (
          <ChipColor
            value={customPriority}
            type={RemediationType.REMEDIATION_PRIORITY}
            sx={{ cursor: "pointer" }}
          />
        )}
      />
    </LabelComponentProp>
  );

  const StatusBox = () => (
    <LabelComponentProp
      textTypography="caption"
      label={"Status"}
      sx={{
        display: "flex",
        alignItems: "left",
      }}
    >
      <StatusChip
        circleColor={
          colors[
            RemediationTypeChip["status"][selectedRemediation?.status].color +
              "_text"
          ]
        }
        label={selectedRemediation?.status}
      />
    </LabelComponentProp>
  );

  const ImpactBox = () => {
    return (
      <Grid item>
        <LabelComponentProp
          sx={{
            display: "flex",
            alignItems: "left",
          }}
          label={"Impact"}
          textTypography="caption"
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ImpactChip
              numberOfEntities={
                fullControl?.impacted_included_entities_count || 0
              }
            />
          </Box>
        </LabelComponentProp>
      </Grid>
    );
  };

  const AttackPathsBox = () => (
    <LabelComponentProp
      label={"Improvement"}
      textTypography="caption"
      sx={{
        display: "flex",
        alignItems: "left",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Typography variant="caption">
          Mitigates {selectedRemediation?.mitigated_scenarios?.length} attack
          paths
          {" (" + selectedRemediation?.scenario_mitigated_percentage + "%)"}
        </Typography>
      </Box>
    </LabelComponentProp>
  );

  // TODO: check if need to delete this component or it is used in the future
  const CostBox = () => (
    <LabelComponentProp label={"Cost"}>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 0,
          width: "90%x",
        }}
      >
        <Box>$</Box>
        <TextField
          variant="standard"
          defaultValue={selectedRemediation?.total_cost}
          InputProps={{
            readOnly: !editCost,
            style: {
              fontSize: "12px",
              height: "100%",
            },
            disableUnderline: !editCost,
          }}
          sx={{
            ...inputSx,
            maxWidth: "80px",
            "& .MuiInput-underline:after": {
              border: "1px solid " + colors.grey_400,
            },
            "& .MuiOutlinedInput-root": {
              border: "1px solid " + colors.grey_400,
              "& fieldset": {
                border: "1px solid " + colors.grey_400,
              },
              "&:hover fieldset": {
                border: "1px solid " + colors.grey_400,
              },
              "&.Mui-focused fieldset": {
                border: "1px solid " + colors.grey_400,
              },
              "& label.Mui-focused": {
                border: "1px solid " + colors.grey_400,
              },
            },
          }}
          type="number"
          fullWidth
          onBlur={(e) => handleCostChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleCostChange(e.target.value);
            }
          }}
        />
        <CustomToolTipWithPermission
          title={null}
          permission={hasPermission}
          placement="top"
        >
          <span>
            <Typography
              mt={"-3px"} //hack to align the text with the title justify content :"flex-start" is not working
              variant="subtitle2"
              fontSize={"12px"}
              color={colors.primary_light}
              sx={{
                textDecorationLine: "underline",
                cursor: "pointer",
                pointerEvents: hasPermission ? "auto" : "none",
                opacity: hasPermission ? 1 : 0.5,
              }}
              onClick={() => setEditCost((prv) => !prv)}
            >
              Edit
            </Typography>
          </span>
        </CustomToolTipWithPermission>
      </div>
    </LabelComponentProp>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Grid container gap={4} alignItems={"center"} rowGap={3} sx={{ pl: 2 }}>
        <ToolBox />
        <Divider orientation="vertical" flexItem />
        <PriorityBox />
        <Divider orientation="vertical" flexItem />
        <StatusBox />
        <Divider orientation="vertical" flexItem />
        <ImpactBox />
        {demoMode && <Divider orientation="vertical" flexItem />}
        {demoMode && <AttackPathsBox />}
      </Grid>
      {demoMode && (
        <Typography
          variant="overline"
          onClick={handlePopoverOpen}
          sx={{
            color: colors.primary_main,
            cursor: "pointer",
          }}
        >
          View Remediation Breakdown
        </Typography>
      )}
      {fullControl && (
        <RemediationBreakdown
          anchorEl={anchorElImpact}
          onClose={handlePopoverClose}
          selectedRemediation={selectedRemediation}
          relatedControl={fullControl}
        />
      )}
    </Box>
  );
};

export default RemediationDetailsSection;
